import { readItemField } from './item-field-reader';
import type { ItemFieldRow, ItemFieldDependencies } from './item-field-reader';
import { createStatusLayerReadContext } from './status-row-construction';
import type { StatusOriginLayerDependencies } from './status-row-construction';
import { createStatusReferenceResolvers } from './status-reference-resolution';
import type { StatusReferenceDependencies } from './status-reference-resolution';
import { layerStatusesOntoCarrier, readStatusPrefetch } from './status-layering';
import type { StatusLayeringMetadata, StatusLayeringReaders } from './status-layering';

export interface StatusLayeringDependencies extends StatusOriginLayerDependencies, Omit<ItemFieldDependencies, 'resolveCarrierRef'> {
    metadata: StatusLayeringMetadata;
    referenceInputs: StatusReferenceDependencies;
    statusPrefetch?: ItemFieldRow | null;
    fetchStatuses: StatusLayeringReaders['fetchStatuses'];
    itemName: StatusLayeringReaders['itemName'];
    campaignContext?: StatusLayeringReaders['campaignContext'];
    campaignSeconds: StatusLayeringReaders['campaignSeconds'];
    casterTeam: StatusLayeringReaders['casterTeam'];
    prefetchHit?: () => void;
    fetchCompleted?: () => void;
}
/** One reader/provider/inventory context and one origin guard per request. */
export function createStatusLayerCompiler(deps: StatusLayeringDependencies):
    (target: ItemFieldRow, mode?: unknown, prefetchedRows?: unknown) => ItemFieldRow {
    const emit = (event: readonly unknown[]): void => { deps.trace?.(event); };
    const context = createStatusLayerReadContext(deps);
    const full: StatusReferenceDependencies['fullItem'] = (id, suppress) => {
        emit(['full-item', id, suppress]);
        const value = deps.referenceInputs.fullItem(id, suppress);
        if (value !== null && typeof value !== 'object') throw new TypeError('Invalid full-item return');
        return value;
    };
    const dynamic: StatusReferenceDependencies['dynamicItem'] = (id, debug) => {
        emit(['dynamic-item', id, debug]);
        const value = deps.referenceInputs.dynamicItem(id, debug);
        if (value === null || typeof value !== 'object') throw new TypeError('Invalid dynamic-item return');
        return value;
    };
    const itemRow = (id: string): unknown => { emit(['item-row', id]); return deps.referenceInputs.itemRow(id); };
    const references = createStatusReferenceResolvers({ itemRow, fullItem: full, dynamicItem: dynamic,
        count: (table, column, id) => { emit(['count', table, column, id]); return deps.referenceInputs.count(table, column, id); },
        determineType: id => { emit(['type', id]); return deps.referenceInputs.determineType(id); },
        reentry: (id, debug) => emit(['log', `[STATUS-ORIGIN] re-entry blocked (cyclic status origin chain, degrades to []): ${id} ctx=${debug}`]),
    });
    const readers: StatusLayeringReaders = { ...context, references,
        itemField: (row, key) => readItemField(row, key, {
            resolveCarrierRef: id => { emit(['reference', id]); return deps.resolveCarrierRef(id); },
            resolveItemName: id => { emit(['name', id]); return deps.itemName(id); },
            ...(deps.resolveInventoryIdentity ? { resolveInventoryIdentity: (id: string) => {
                emit(['inventory-identity', id]); return deps.resolveInventoryIdentity!(id);
            } } : {}),
        }),
        prefetch: (mode, id) => { emit(['status-prefetch', mode, id]); return readStatusPrefetch(deps.statusPrefetch ?? null, mode, id); },
        fetchStatuses: filters => { emit(['fetch-statuses', filters]); return deps.fetchStatuses(filters); },
        prefetchHit: deps.prefetchHit, fetchCompleted: deps.fetchCompleted,
        itemRow, fullItem: full, dynamicItem: dynamic,
        itemName: id => { emit(['name', id]); return deps.itemName(id); },
        ...(deps.campaignContext ? { campaignContext: (row: ItemFieldRow) => { emit(['campaign-context']); return deps.campaignContext!(row); } } : {}),
        campaignSeconds: time => { emit(['campaign-seconds', time]); return deps.campaignSeconds(time); },
        casterTeam: id => { emit(['caster-team', id]); return deps.casterTeam(id); },
        warning: message => emit(['warning', message]), log: message => emit(['log', message]),
    };
    return (target, mode = 'target', prefetched = null) => layerStatusesOntoCarrier(target, mode, prefetched, deps.metadata, readers);
}
